import { createContext, useState, useContext, useEffect, useCallback, useRef } from "react";
import { endSession, needsPasswordChange, readUser, startSessionTimers } from "../services/session";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import OilBarrelIcon from "@mui/icons-material/OilBarrel";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import TrainIcon from "@mui/icons-material/Train";
import DevicesIcon from "@mui/icons-material/Devices";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import FactoryIcon from "@mui/icons-material/Factory";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import KitchenIcon from "@mui/icons-material/Kitchen";
import BoltIcon from "@mui/icons-material/Bolt";
import MemoryIcon from "@mui/icons-material/Memory";
import { api } from "../services/api";
import riskMessageService from "../services/riskMessageService";

const AppContext = createContext();

export const PROJECT_TYPE_ICONS = {
  motorcycle: TwoWheelerIcon,
  oil: OilBarrelIcon,
  bicycle: DirectionsBikeIcon,
  auto: DirectionsCarIcon,
  airplane: RocketLaunchIcon,
  train: TrainIcon,
  medical: LocalHospitalIcon,
  manufacturing: FactoryIcon,
  naval: DirectionsBoatIcon,
  appliance: KitchenIcon,
  electrical: BoltIcon,
  electronics: MemoryIcon,
  default: DevicesIcon,
};

export function AppProvider({ children }) {
  // State for Risk and Error messages
  const [riskAndErrorMessages, setRiskAndErrorMessages] = useState([]);
  const [currentRiskConversationId, setCurrentRiskConversationId] = useState(null);
  const [isRiskAndErrorLoading, setIsRiskAndErrorLoading] = useState(false);
  const [riskAndErrorError, setRiskAndErrorError] = useState(null);

  // Initialize user state from localStorage if it exists
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem("user");
      const parsed = savedUser ? JSON.parse(savedUser) : null;
      // a first-login session stays on /login until the password is changed
      return needsPasswordChange(parsed) ? null : parsed;
    } catch (error) {
      console.error("Error parsing user from localStorage:", error);
      return null;
    }
  });

  // Add projects state
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Only the latest fetch ends loading; an older one settling first showed "Create Your First Project"
  const projectsRequestRef = useRef(0);

  // Add organization state
  const [organization, setOrganization] = useState(() => {
    try {
      const savedOrg = localStorage.getItem("organization");
      return savedOrg ? JSON.parse(savedOrg) : null;
    } catch (error) {
      console.error("Error parsing organization from localStorage:", error);
      return null;
    }
  });

  // Add isExecUser check
  const isExecUser = user?.role === "exec" || user?.role === "cbExec";

  // Feature flags
  const [featureFlags, setFeatureFlags] = useState({
    SUPPLIER_PORTAL_ENABLED: process.env.REACT_APP_SUPPLIER_PORTAL_ENABLED !== "false", // Default to true unless explicitly disabled
    MARKET_INTEL_ENABLED: process.env.REACT_APP_MARKET_INTEL_ENABLED === "true", // off unless explicitly enabled
  });

  // Fetch projects when user is authenticated
  useEffect(() => {
    const fetchProjects = async () => {
      if (user) {
        const request = ++projectsRequestRef.current;
        try {
          setIsLoading(true);
          const projectsData = await api.getProjects();
          if (request === projectsRequestRef.current) {
            setProjects(projectsData);
          }
        } catch (error) {
          console.error("Error fetching projects:", error);
        } finally {
          if (request === projectsRequestRef.current) {
            setIsLoading(false);
          }
        }
      }
    };
    fetchProjects();
  }, [user]);

  // For executive users, automatically set the first project if none is selected
  useEffect(() => {
    if (isExecUser && projects && projects.length > 0 && !currentProjectId) {
      const firstProject = projects[0];
      setCurrentProject({
        id: firstProject.id,
        name: firstProject.title,
        type: firstProject.projectType,
        status: firstProject.status || "active",
      });
      setCurrentProjectId(firstProject.id);
    }
  }, [isExecUser, projects]);

  // Fetch organization data when user is available but organization is missing or mismatched
  useEffect(() => {
    const fetchOrganization = async () => {
      if (user?.org_id && (!organization || organization.org_id !== user.org_id)) {
        try {
          const orgData = await api.getOrg(user.org_id);
          setOrganization(orgData);
          localStorage.setItem("organization", JSON.stringify(orgData));
        } catch (error) {
          console.error("Error fetching organization:", error);
          setOrganization(null);
          localStorage.removeItem("organization");
        }
      }
    };
    fetchOrganization();
  }, [user?.org_id, organization?.org_id]);

  // Update localStorage whenever user state changes
  const updateUser = useCallback(async (userData) => {
    // never let a stale copy overwrite tokens that were rotated since it was read
    const stored = readUser();
    if (userData && stored?.token) {
      userData = {
        ...userData,
        token: stored.token,
        refresh_token: stored.refresh_token,
        expires_in: stored.expires_in,
        idle_timeout_seconds: stored.idle_timeout_seconds,
        tokenIssuedAt: stored.tokenIssuedAt,
      };
    }
    setUser(userData);
    if (userData) {
      localStorage.setItem("user", JSON.stringify(userData));

      // Fetch organization data if user has an org_id
      if (userData.org_id) {
        try {
          const orgData = await api.getOrg(userData.org_id);
          setOrganization(orgData);
          localStorage.setItem("organization", JSON.stringify(orgData));
        } catch (error) {
          console.error("Error fetching organization:", error);
          setOrganization(null);
          localStorage.removeItem("organization");
        }
      }
    } else {
      localStorage.removeItem("user");
      localStorage.removeItem("organization");
      setOrganization(null);
      setProjects([]);
    }
  }, []);

  // Add function to update projects
  const updateProjects = useCallback((newProjects) => {
    setProjects(newProjects);
    // Persist projects in localStorage
    localStorage.setItem("projects", JSON.stringify(newProjects));
  }, []);

  // Add effect to load projects from localStorage on mount
  useEffect(() => {
    try {
      const savedProjects = localStorage.getItem("projects");
      if (savedProjects) {
        const parsedProjects = JSON.parse(savedProjects);
        setProjects(parsedProjects);
      }
    } catch (error) {
      console.error("Error loading projects from localStorage:", error);
    }
  }, []);

  // Dashboard states
  const [needsAttentionPage, setNeedsAttentionPage] = useState(0);
  const [assignedPage, setAssignedPage] = useState(0);

  // Sidebar states
  const [codifierOpen, setCodifierOpen] = useState(true);
  const [approverOpen, setApproverOpen] = useState(true);
  const [reporterOpen, setReporterOpen] = useState(true);
  const [applicatorOpen, setApplicatorOpen] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Raise a Request dialog (global, persists across routes)
  const [raiseRequestOpen, setRaiseRequestOpen] = useState(false);

  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const [currentConversation, setCurrentConversation] = useState(null);

  // Add codifierMessages state
  const [codifierMessages, setCodifierMessages] = useState([]);

  // Classification context — persistent vehicle/regulation scope with lock/unlock
  const [conversationClassification, setConversationClassification] = useState(null);

  // Add chat title state
  const [chatTitle, setChatTitle] = useState("");

  // Add function to update conversation title
  const updateConversationTitle = useCallback((title) => {
    setChatTitle(title);
  }, []);

  // Add function to initialize conversation
  const initializeConversation = useCallback(
    async (projectId) => {
      if (!projectId) {
        return null;
      }

      try {
        // First get the conversation
        const { messages: carried, ...conversation } = await api.initializeConversation(
          projectId,
          user?.user_id
        );
        setCurrentConversation(conversation);

        // Set the conversation title
        if (conversation.title) {
          updateConversationTitle(conversation.title);
        } else {
          updateConversationTitle("CertGPT");
        }

        // Load messages for the conversation
        // a backend without include_messages answers without them
        const messages = Array.isArray(carried)
          ? carried
          : await api.getConversationMessages(conversation.id);
        const formattedMessages = messages.map((msg) => ({
          isUser: msg.message_type === "user",
          text: msg.content,
          timestamp: msg.metadata?.timestamp,
          fileCards: msg.metadata?.file_cards || undefined,
          ...msg,
        }));

        // an empty thread clears the previous project's messages
        setCodifierMessages(formattedMessages);

        return conversation;
      } catch (error) {
        console.error("Error initializing conversation:", error);
        return null;
      }
    },
    [user?.user_id, updateConversationTitle]
  );

  // Functions for Risk and Error messages - FIXED
  const fetchRiskAndErrorMessages = useCallback(async (conversationId) => {
    if (!conversationId) {
      return;
    }

    setIsRiskAndErrorLoading(true);
    setRiskAndErrorError(null);

    try {
      const messages = await riskMessageService.getMessages(conversationId);

      // Format messages for UI
      const formattedMessages = (messages || []).map((msg) => ({
        id: msg.id,
        content: msg.content,
        text: msg.content, // For UI compatibility
        isUser: msg.message_type === "isUser",
        message_type: msg.message_type,
        created_at: msg.created_at,
        user_id: msg.user_id,
        conversation_id: msg.conversation_id,
        message_metadata: msg.message_metadata || {},
        // Add any additional message properties
        ...msg,
      }));

      setRiskAndErrorMessages(formattedMessages);
      setRiskAndErrorError(null);
    } catch (err) {
      console.error("Error fetching risk messages:", err);
      setRiskAndErrorError("Failed to fetch messages.");
      // Set empty array on error to prevent UI issues
      setRiskAndErrorMessages([]);
    } finally {
      setIsRiskAndErrorLoading(false);
    }
  }, []);

  const sendRiskAndErrorMessage = useCallback(
    async (message) => {
      if (!message?.trim()) {
        return;
      }

      // Use projectId directly as conversation ID — consistent with RisksAndErrors.jsx
      let conversationId = currentProjectId || currentRiskConversationId;
      if (!conversationId) {
        return;
      }
      if (conversationId !== currentRiskConversationId) {
        setCurrentRiskConversationId(conversationId);
      }

      // Optimistic UI update
      const tempId = `temp-${Date.now()}`;
      const userMessage = {
        id: tempId,
        content: message,
        text: message, // For UI compatibility
        isUser: true,
        message_type: "isUser",
        created_at: new Date().toISOString(),
        user_id: user?.user_id || "anonymous",
        conversation_id: conversationId,
        message_metadata: {},
      };

      setRiskAndErrorMessages((prevMessages) => [...prevMessages, userMessage]);

      try {
        const response = await riskMessageService.createMessage(conversationId, {
          content: message,
          message_type: "isUser",
          process_with_agent: true,
          metadata: { conversation_id: conversationId },
        });

        // Update conversation ID if response provides a new one
        if (
          response?.user_message?.conversation_id &&
          response.user_message.conversation_id !== conversationId
        ) {
          setCurrentRiskConversationId(response.user_message.conversation_id);
          conversationId = response.user_message.conversation_id;
        }

        if (!response || !response.user_message) {
          throw new Error("Invalid response from server");
        }

        const newMessages = [];

        // Format user message from response
        const finalUserMessage = {
          id: response.user_message.id,
          content: response.user_message.content,
          text: response.user_message.content,
          isUser: true,
          message_type: response.user_message.message_type,
          created_at: response.user_message.created_at,
          user_id: response.user_message.user_id,
          conversation_id: response.user_message.conversation_id,
          message_metadata: response.user_message.message_metadata || {},
        };

        // Format agent message if present
        if (response.agent_message) {
          const agentMessage = {
            id: response.agent_message.id,
            content: response.agent_message.content,
            text: response.agent_message.content,
            isUser: false,
            isAgent: true,
            message_type: response.agent_message.message_type,
            created_at: response.agent_message.created_at,
            user_id: response.agent_message.user_id,
            conversation_id: response.agent_message.conversation_id,
            message_metadata: response.agent_message.message_metadata || {},
            // Include analysis data
            tableData: response.table_data || {},
            summaryStats: response.summary_stats || {},
            analysis_metadata: response.analysis_metadata || {},
          };
          newMessages.push(agentMessage);
        }

        // Replace temp message with final messages
        setRiskAndErrorMessages((prevMessages) => [
          ...prevMessages.filter((m) => m.id !== tempId),
          finalUserMessage,
          ...newMessages,
        ]);

        setRiskAndErrorError(null);
        return response;
      } catch (err) {
        console.error("Error sending risk/error message:", err);
        setRiskAndErrorError("Failed to send message: " + (err.message || "Unknown error"));
        // Revert optimistic update on error
        setRiskAndErrorMessages((prevMessages) => prevMessages.filter((m) => m.id !== tempId));
        throw err;
      }
    },
    [currentRiskConversationId, currentProjectId, user?.user_id]
  );

  // Initialize risk conversation when project changes - FIXED
  useEffect(() => {
    let mounted = true;

    const initRiskConversation = async () => {
      if (currentProjectId && user?.user_id && mounted) {
        try {
          // the CertGPT chat initializes its own conversation; doing it here too fetched the
          // whole chat (3.2 MB on one dev chat) a second time on every project open
          // Use projectId directly as conversation ID — same as RisksAndErrors.jsx
          const riskConversationId = currentProjectId;
          setCurrentRiskConversationId(riskConversationId);

          // Fetch existing messages for this risk conversation
          if (mounted) {
            await fetchRiskAndErrorMessages(riskConversationId);
          }
        } catch (error) {
          console.error("Error initializing risk conversation:", error);
          if (mounted) {
            setRiskAndErrorError("Failed to initialize conversation");
          }
        }
      }
    };

    initRiskConversation();

    return () => {
      mounted = false;
    };
  }, [currentProjectId, user?.user_id, fetchRiskAndErrorMessages]);

  // Handle logout: revoke the server-side session, then clear local state
  const handleLogout = useCallback(() => {
    endSession();
    setUser(null);
    setCurrentConversation(null);
    setCodifierMessages([]);
    setChatTitle("");
    setProjects([]);
    // Clear risk messages
    setRiskAndErrorMessages([]);
    setCurrentRiskConversationId(null);
    setRiskAndErrorError(null);
  }, []);

  // Proactive token renewal and idle logout while someone is signed in
  useEffect(() => {
    if (!user?.refresh_token) {
      return undefined;
    }
    const stop = startSessionTimers(() => {
      setUser(null);
      window.location.href = "/login";
    });
    // a rotation (this tab or another) replaces the tokens the context hands to components
    const onUpdated = (e) => setUser((prev) => (prev ? { ...prev, ...e.detail } : prev));
    window.addEventListener("session:updated", onUpdated);
    return () => {
      stop();
      window.removeEventListener("session:updated", onUpdated);
    };
  }, [user?.refresh_token]);

  const value = {
    riskAndErrorMessages,
    isRiskAndErrorLoading,
    riskAndErrorError,
    setRiskAndErrorError,
    fetchRiskAndErrorMessages,
    sendRiskAndErrorMessage,
    currentRiskConversationId,
    setCurrentRiskConversationId,
    user,
    updateUser,
    isExecUser,
    needsAttentionPage,
    setNeedsAttentionPage,
    assignedPage,
    setAssignedPage,
    codifierOpen,
    setCodifierOpen,
    approverOpen,
    setApproverOpen,
    reporterOpen,
    setReporterOpen,
    applicatorOpen,
    setApplicatorOpen,
    currentProject,
    setCurrentProject,
    currentProjectId,
    setCurrentProjectId,
    currentConversation,
    setCurrentConversation,
    handleLogout,
    sidebarOpen,
    setSidebarOpen,
    raiseRequestOpen,
    setRaiseRequestOpen,
    codifierMessages,
    setCodifierMessages,
    conversationClassification,
    setConversationClassification,
    chatTitle,
    setChatTitle,
    organization,
    setOrganization,
    updateConversationTitle,
    initializeConversation,
    projects,
    updateProjects,
    isLoading,
    PROJECT_TYPE_ICONS,
    featureFlags,
    setFeatureFlags,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};
